/**
 * Nominal ("branded") typing helper.
 * Prevents mixing structurally identical primitives such as a DeviceId and a NetworkId.
 */
declare const brandSymbol: unique symbol;
export type Brand<T, B extends string> = T & { readonly [brandSymbol]: B };

export type DeviceId = Brand<string, 'DeviceId'>;
export type NetworkId = Brand<string, 'NetworkId'>;

/** Terminal identifiers are declared by device definitions (e.g. `'run'`, `'out'`). */
export type TerminalId = string;

export const asDeviceId = (value: string): DeviceId => value as DeviceId;
export const asNetworkId = (value: string): NetworkId => value as NetworkId;

let fallbackCounter = 0;

/**
 * Standard RFC4122 v4 UUID generator that works reliably in ALL environments,
 * including non-secure contexts (plain HTTP over IP/LAN/Debian server) where
 * `crypto.randomUUID` is not exposed by modern browsers.
 */
export function safeRandomUUID(): string {
  if (typeof globalThis.crypto !== 'undefined' && typeof globalThis.crypto.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** Generates a unique identifier with a readable prefix, e.g. `dev_3f2a…`. */
export function createId(prefix: string): string {
  const random =
    typeof globalThis.crypto !== 'undefined' && typeof globalThis.crypto.randomUUID === 'function'
      ? globalThis.crypto.randomUUID().replace(/-/g, '').slice(0, 12)
      : `${Date.now().toString(36)}${(fallbackCounter++).toString(36)}`;
  return `${prefix}_${random}`;
}
